import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function EnablePushButton() {
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    const check = async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setSupported(false);
        return;
      }
      const registration = await navigator.serviceWorker.ready.catch(() => null);
      const sub = await registration?.pushManager.getSubscription().catch(() => null);
      setSubscribed(!!sub);
    };
    check();
  }, []);

  const enablePush = async () => {
    setBusy(true);
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("Pelayar anda tidak menyokong notifikasi push");
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("Kebenaran notifikasi tidak diberikan");

      const { data } = await api.get("/push/public-key");
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }
      const json = subscription.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setSubscribed(true);
      toast.success("Notifikasi push diaktifkan");
    } catch (e) {
      toast.error(e.message || "Gagal mengaktifkan notifikasi");
    } finally {
      setBusy(false);
    }
  };

  const disablePush = async () => {
    setBusy(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.delete("/push/unsubscribe", { params: { endpoint: subscription.endpoint } });
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifikasi push dimatikan");
    } catch (e) {
      toast.error("Gagal mematikan notifikasi");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return <p className="text-xs text-piks-muted" data-testid="push-unsupported-msg">Pelayar anda tidak menyokong notifikasi push.</p>;
  }

  return subscribed ? (
    <Button type="button" variant="outline" onClick={disablePush} disabled={busy} data-testid="disable-push-btn" className="gap-2">
      <BellOff className="w-4 h-4" /> {busy ? "Memproses..." : "Matikan Notifikasi Push"}
    </Button>
  ) : (
    <Button type="button" onClick={enablePush} disabled={busy} data-testid="enable-push-btn" className="gap-2 bg-piks-black hover:bg-piks-black/90">
      <Bell className="w-4 h-4" /> {busy ? "Memproses..." : "Aktifkan Notifikasi Push"}
    </Button>
  );
}
